/* Customer-service forms rendered from the support-form relay config.
 * Mount: <div data-support-form="after_sale" data-store="torchstar" data-endpoint="https://…"></div>
 * Tabs: a wrapper with data-sf-tabs (and style="--sf-accent: …") around the
 * theme's .button-container / .tab-button[data-target] / .tab-content markup
 * becomes an accessible tablist with a sliding indicator.
 * Files go straight to Google Cloud Storage through signed URLs; the relay
 * then opens a Re:amaze conversation with the attachments. */
(function () {
  'use strict';

  var configCache = {};
  var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
  var AUTOCOMPLETE = { 'first name': 'given-name', 'last name': 'family-name', 'street address': 'street-address', city: 'address-level2', state: 'address-level1', 'zip code': 'postal-code' };
  var ICONS = {
    check: '<path d="m5 12.5 4.5 4.5L19 7.5"/>',
    x: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>',
    alert: '<circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5"/><path d="M12 16.4v.1"/>',
    chevron: '<path d="m6.5 9.5 5.5 5.5 5.5-5.5"/>',
    upload: '<path d="M12 15.5V4.5"/><path d="m7.5 9 4.5-4.5L16.5 9"/><path d="M4.5 15.5v3a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-3"/>',
  };

  function reduced() {
    return !!(motion && motion.matches);
  }

  function loadConfig(endpoint, store) {
    var key = endpoint + '|' + store;
    if (!configCache[key]) {
      configCache[key] = fetch(endpoint + '/v1/forms/' + encodeURIComponent(store)).then(function (r) {
        if (!r.ok) throw new Error('config ' + r.status);
        return r.json();
      });
    }
    return configCache[key];
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === undefined || v === null || v === false) return;
        if (k === 'text') node.textContent = v;
        else if (k === 'className') node.className = v;
        else if (k === 'html') node.innerHTML = v; // only our static icon markup
        else node.setAttribute(k, v === true ? '' : v);
      });
    }
    (children || []).forEach(function (c) {
      if (c) node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function icon(name) {
    return '<svg class="sf-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + ICONS[name] + '</svg>';
  }

  // Restart a CSS animation class; it is dropped again when the animation ends.
  function restart(node, cls) {
    node.classList.remove(cls);
    void node.offsetWidth;
    node.classList.add(cls);
    node.addEventListener('animationend', function done(e) {
      if (e.target !== node) return;
      node.classList.remove(cls);
      node.removeEventListener('animationend', done);
    });
  }

  function focusQuietly(node) {
    try { node.focus({ preventScroll: true }); } catch (e) { node.focus(); }
  }

  function scrollToView(node, block) {
    node.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: block });
  }

  function extOf(name) {
    var m = /\.([A-Za-z0-9]+)$/.exec(name || '');
    return m ? m[1].toLowerCase() : '';
  }

  function formatSize(bytes) {
    return bytes >= 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  // RFC 4122 version 4; the relay rejects anything else.
  function newId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var b = window.crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return (x + 0x100).toString(16).slice(1); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  // --sf-accent is the brand colour. White text needs 4.5:1 on it, so light
  // accents (LeonLite grey) get a darker --sf-ink for text and the button.
  function luminance(c) {
    var v = c.map(function (x) {
      x /= 255;
      return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
  }
  function applyAccent(node) {
    var probe = el('span');
    probe.style.display = 'none';
    probe.style.color = 'var(--sf-accent, #a11421)';
    node.appendChild(probe);
    var m = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(getComputedStyle(probe).color);
    node.removeChild(probe);
    if (!m) return;
    var rgb = [+m[1], +m[2], +m[3]];
    var ink = rgb.slice();
    for (var i = 0; i < 40 && 1.05 / (luminance(ink) + 0.05) < 4.5; i++) {
      ink = ink.map(function (x) { return Math.floor(x * 0.95); });
    }
    node.style.setProperty('--sf-ink', 'rgb(' + ink.join(', ') + ')');
    node.style.setProperty('--sf-ring', 'rgba(' + rgb.join(', ') + ', 0.2)');
    node.style.setProperty('--sf-tint', 'rgba(' + rgb.join(', ') + ', 0.06)');
  }

  function renderForm(mount, store, endpoint, formKey, form) {
    var uid = 'sf-' + store + '-' + formKey;
    // One id per filled form, reused on every retry, so a lost response can
    // never become a second ticket. The form is replaced after success.
    var state = { files: {}, submissionId: newId(), busy: false };
    var fieldNodes = {};
    var byId = {};
    form.fields.forEach(function (f) { byId[f.id] = f; });

    var titleId = uid + '-title';
    var root = el('form', { className: 'sf-form', novalidate: true, 'aria-labelledby': form.title ? titleId : null });
    if (form.title) root.appendChild(el('h3', { className: 'sf-title', id: titleId, text: form.title }));
    if (form.description) root.appendChild(el('p', { className: 'sf-description', text: form.description }));
    root.appendChild(el('p', { className: 'sf-note', text: 'All fields are required unless marked optional.' }));

    var grid = el('div', { className: 'sf-grid' });
    root.appendChild(grid);

    function labelFor(field, forId) {
      return el(forId ? 'label' : 'legend', { className: 'sf-label', for: forId || null }, [
        field.label,
        field.required ? null : el('span', { className: 'sf-optional', text: 'Optional' }),
      ]);
    }

    form.fields.forEach(function (field) {
      var id = uid + '-' + field.id;
      var errorId = id + '-error';
      var wrap = el('div', {
        className: 'sf-field sf-field--' + field.type,
        'data-field': field.id,
        'data-width': field.type === 'heading' ? '100' : String(Math.round(field.width || 100)),
      });
      var error = el('p', { className: 'sf-error', id: errorId, 'aria-live': 'polite' });
      var control;

      if (field.type === 'heading') {
        wrap.appendChild(el('h4', { className: 'sf-heading', text: field.label }));
        error = null;
      } else if (field.type === 'radio') {
        var cards = el('div', { className: 'sf-cards sf-shake-target' });
        field.options.forEach(function (o, i) {
          var oid = id + '-' + i;
          cards.appendChild(el('label', { className: 'sf-card', for: oid }, [
            el('input', { type: 'radio', className: 'sf-card-input', id: oid, name: id, value: o.value, 'aria-describedby': errorId }),
            el('span', { className: 'sf-card-body' }, [
              el('span', { className: 'sf-card-text', text: o.label }),
              el('span', { className: 'sf-tick', 'aria-hidden': 'true', html: '<i></i>' + icon('check') }),
            ]),
          ]));
        });
        wrap.appendChild(el('fieldset', { className: 'sf-fieldset' }, [labelFor(field), cards]));
      } else if (field.type === 'file') {
        state.files[field.id] = [];
        wrap.appendChild(labelFor(field, id));
        var input = el('input', {
          type: 'file',
          id: id,
          className: 'sf-file-input',
          multiple: field.multiple,
          accept: field.extensions.map(function (e) { return '.' + e; }).join(','),
          'aria-describedby': (field.hint ? id + '-hint ' : '') + errorId,
        });
        var drop = el('label', { className: 'sf-drop sf-shake-target', for: id }, [
          el('span', { className: 'sf-drop-icon', html: icon('upload') }),
          el('span', { className: 'sf-drop-title' }, [el('strong', { text: 'Drop files here' }), ' or ', el('span', { className: 'sf-drop-browse', text: 'browse' })]),
          el('span', { className: 'sf-drop-meta', text: 'Up to ' + field.maxFiles + ' files · ' + field.maxSizeMB + ' MB each' }),
        ]);
        var list = el('ul', { className: 'sf-file-list', 'aria-label': 'Selected files' });
        wrap.appendChild(input);
        wrap.appendChild(drop);
        if (field.hint) wrap.appendChild(el('p', { className: 'sf-hint', id: id + '-hint', text: field.hint }));
        wrap.appendChild(list);
        wireFiles(field, input, drop, list);
      } else {
        wrap.appendChild(labelFor(field, id));
        var placeholder = field.placeholder && field.placeholder !== field.label ? field.placeholder : null;
        if (field.type === 'textarea') {
          control = el('textarea', { id: id, rows: '5', placeholder: placeholder, maxlength: '5000' });
        } else if (field.type === 'select') {
          control = el('select', { id: id }, [el('option', { value: '', text: field.placeholder || 'Select…' })].concat(field.options.map(function (o) {
            return el('option', { value: o.value, text: o.label });
          })));
        } else {
          var type = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : 'text';
          control = el('input', {
            id: id,
            type: type,
            placeholder: placeholder,
            maxlength: String(field.maxLength || 500),
            autocomplete: type === 'email' ? 'email' : type === 'tel' ? 'tel' : AUTOCOMPLETE[String(field.label).toLowerCase()] || null,
            min: type === 'number' ? '0' : null,
            inputmode: type === 'number' ? 'numeric' : null,
          });
        }
        control.setAttribute('aria-describedby', errorId);
        if (field.required) control.setAttribute('aria-required', 'true');
        var box = el('div', { className: 'sf-box sf-shake-target' + (field.type === 'select' ? ' sf-box--select' : '') }, [control]);
        if (field.type === 'select') box.insertAdjacentHTML('beforeend', icon('chevron').replace('class="sf-icon"', 'class="sf-icon sf-chevron"'));
        else if (field.type !== 'textarea') box.appendChild(el('span', { className: 'sf-valid', 'aria-hidden': 'true', html: icon('check') }));
        wrap.appendChild(box);
        if (field.description) wrap.appendChild(el('p', { className: 'sf-hint', text: field.description }));
      }
      if (error) wrap.appendChild(error);
      fieldNodes[field.id] = wrap;
      grid.appendChild(wrap);
    });

    // Honeypot: real visitors never see or fill it.
    var trapInput = el('input', { type: 'text', id: uid + '-website', name: 'website', tabindex: '-1', autocomplete: 'off' });
    root.appendChild(el('div', { className: 'sf-trap', 'aria-hidden': 'true' }, [el('label', { for: uid + '-website', text: 'Website' }), trapInput]));

    var status = el('p', { className: 'sf-status', role: 'status', 'aria-live': 'polite' });
    var submitLabel = el('span', { className: 'sf-submit-label', text: form.submitText || 'Submit' });
    var submit = el('button', { type: 'submit', className: 'sf-submit', 'data-state': 'idle' }, [
      el('span', { className: 'sf-spinner', 'aria-hidden': 'true' }),
      el('span', { className: 'sf-submit-done', 'aria-hidden': 'true', html: icon('check') }),
      submitLabel,
    ]);
    root.appendChild(el('div', { className: 'sf-footer' }, [status, submit]));

    function setStatus(text, isError, quiet) {
      status.textContent = text;
      status.classList.toggle('is-error', !!isError);
      status.classList.toggle('sf-status--quiet', !!quiet);
    }

    function setButton(name, text) {
      submit.setAttribute('data-state', name);
      if (name === 'idle') submit.removeAttribute('aria-disabled');
      else submit.setAttribute('aria-disabled', 'true');
      if (submitLabel.textContent !== text) {
        submitLabel.textContent = text;
        restart(submitLabel, 'sf-swap');
      }
    }

    // ── files ──
    function wireFiles(field, input, drop, list) {
      var dropIcon = drop.querySelector('.sf-drop-icon');
      function magnet(e, strength) {
        var r = drop.getBoundingClientRect();
        if (!r.width || !r.height) return;
        dropIcon.style.setProperty('--sf-mx', (((e.clientX - r.left) / r.width - 0.5) * strength * 2).toFixed(1) + 'px');
        dropIcon.style.setProperty('--sf-my', (((e.clientY - r.top) / r.height - 0.5) * strength * 1.4).toFixed(1) + 'px');
      }
      function rest() {
        dropIcon.style.setProperty('--sf-mx', '0px');
        dropIcon.style.setProperty('--sf-my', '0px');
      }
      ['dragover', 'dragenter'].forEach(function (t) {
        drop.addEventListener(t, function (e) {
          e.preventDefault();
          drop.classList.add('is-over');
          magnet(e, 12);
        });
      });
      drop.addEventListener('dragleave', function (e) {
        if (drop.contains(e.relatedTarget)) return;
        drop.classList.remove('is-over');
        rest();
      });
      drop.addEventListener('drop', function (e) {
        e.preventDefault();
        drop.classList.remove('is-over');
        rest();
        if (e.dataTransfer && e.dataTransfer.files) addFiles(field, list, e.dataTransfer.files);
      });
      drop.addEventListener('pointermove', function (e) { if (e.pointerType === 'mouse') magnet(e, 6); });
      drop.addEventListener('pointerleave', rest);
      input.addEventListener('change', function () {
        addFiles(field, list, input.files);
        input.value = '';
      });
    }

    function addFiles(field, list, fileList) {
      clearError(field);
      var files = state.files[field.id];
      var problems = [];
      Array.prototype.forEach.call(fileList, function (f) {
        var problem = null;
        if (field.extensions.indexOf(extOf(f.name)) === -1) problem = f.name + ': file type not allowed';
        else if (f.size > field.maxSizeMB * 1048576) problem = f.name + ': larger than ' + field.maxSizeMB + ' MB';
        else if (files.length >= field.maxFiles) problem = 'At most ' + field.maxFiles + ' files';
        if (problem) {
          if (problems.indexOf(problem) === -1) problems.push(problem);
          return;
        }
        var item = fileItem(field, f);
        files.push(item);
        list.appendChild(item.row);
      });
      while (!field.multiple && files.length > 1) removeFile(field, files[0], true);
      if (problems.length) showError(field, problems.join('. '));
    }

    function fileItem(field, file) {
      var item = { file: file, url: null };
      var ext = extOf(file.name);
      var thumb = el('span', { className: 'sf-thumb' });
      if (/^(jpe?g|png|gif|webp)$/.test(ext) && window.URL && URL.createObjectURL) {
        item.url = URL.createObjectURL(file);
        thumb.appendChild(el('img', { src: item.url, alt: '' }));
      } else {
        thumb.textContent = ext.toUpperCase();
      }
      item.meta = el('span', { className: 'sf-file-meta', text: formatSize(file.size) });
      item.bar = el('i');
      var remove = el('button', { type: 'button', className: 'sf-file-remove', 'aria-label': 'Remove ' + file.name, html: icon('x') });
      remove.addEventListener('click', function () { removeFile(field, item, false); });
      item.row = el('li', { className: 'sf-file sf-file-in', 'data-progress': '0' }, [
        thumb,
        el('span', { className: 'sf-file-text' }, [el('span', { className: 'sf-file-name', title: file.name, text: file.name }), item.meta]),
        remove,
        el('span', { className: 'sf-progress', 'aria-hidden': 'true' }, [item.bar]),
      ]);
      return item;
    }

    function removeFile(field, item, quiet) {
      var files = state.files[field.id];
      var i = files.indexOf(item);
      if (i === -1) return;
      files.splice(i, 1);
      if (item.url) URL.revokeObjectURL(item.url);
      var row = item.row;
      row.classList.add('sf-file-out');
      setTimeout(function () { if (row.parentNode) row.parentNode.removeChild(row); }, reduced() ? 0 : 160);
      if (quiet) return;
      var next = files[i] || files[i - 1];
      focusQuietly(next ? next.row.querySelector('.sf-file-remove') : document.getElementById(uid + '-' + field.id));
    }

    function setProgress(item, p, label) {
      var pct = Math.round(p * 100);
      item.bar.style.transform = 'scaleX(' + p + ')';
      item.row.setAttribute('data-progress', String(pct));
      item.row.classList.toggle('is-done', p >= 1);
      item.meta.textContent = formatSize(item.file.size) + (label ? ' · ' + label : p >= 1 ? ' · Uploaded' : ' · ' + pct + '%');
    }

    // A PUT that sends nothing for this long is aborted and goes through the normal failure path.
    // It is a stall limit, not a total limit, so slow but moving uploads of large files still finish.
    var UPLOAD_STALL_MS = 60000;

    // PUT to the signed URL with exactly the headers the relay returned;
    // XMLHttpRequest because fetch() reports no upload progress.
    function putFile(up, item) {
      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        var stall = 0;
        function arm() {
          clearTimeout(stall);
          stall = setTimeout(function () { xhr.abort(); }, UPLOAD_STALL_MS);
        }
        xhr.open('PUT', up.url);
        Object.keys(up.headers || {}).forEach(function (k) { xhr.setRequestHeader(k, up.headers[k]); });
        xhr.upload.onprogress = function (e) {
          arm();
          if (e.lengthComputable && e.total) setProgress(item, Math.min(e.loaded / e.total, 0.99));
        };
        xhr.onloadend = function () { clearTimeout(stall); };
        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            setProgress(item, 1);
            resolve();
          } else {
            reject(new Error('Upload failed for ' + item.file.name + '. Please try again.'));
          }
        };
        // Same branch as a failed fetch(): shown as a connection problem.
        xhr.onerror = xhr.onabort = xhr.ontimeout = function () { reject(new TypeError('Network error')); };
        setProgress(item, 0);
        arm();
        xhr.send(item.file);
      });
    }

    // ── values, visibility, validation ──
    function valueOf(field) {
      var id = uid + '-' + field.id;
      if (field.type === 'radio') {
        var checked = root.querySelector('input[name="' + id + '"]:checked');
        return checked ? checked.value : '';
      }
      var node = document.getElementById(id);
      return node ? String(node.value || '').trim() : '';
    }

    function isVisible(field) {
      if (!field.showWhen) return true;
      var dep = byId[field.showWhen.field];
      return !!dep && isVisible(dep) && field.showWhen.in.indexOf(valueOf(dep)) !== -1;
    }

    function refresh() {
      var shown = 0;
      form.fields.forEach(function (f) {
        var node = fieldNodes[f.id];
        var visible = isVisible(f);
        if (visible && node.hidden) {
          node.hidden = false;
          node.style.setProperty('--sf-i', String(Math.min(shown++, 10)));
          restart(node, 'sf-reveal');
        } else if (!visible && !node.hidden) {
          node.hidden = true;
          clearError(f);
        }
      });
    }

    function checkField(f) {
      if (f.type === 'heading' || !isVisible(f)) return '';
      if (f.type === 'file') return f.required && state.files[f.id].length === 0 ? f.label + ' is required' : '';
      var v = valueOf(f);
      if (!v) return f.required ? f.label + ' is required' : '';
      if (f.type === 'email' && !EMAIL_RE.test(v)) return 'Email address is invalid';
      return '';
    }

    function controlsOf(wrap) {
      return Array.prototype.slice.call(wrap.querySelectorAll('input, select, textarea'));
    }

    function showError(field, message, shake) {
      var wrap = fieldNodes[field.id];
      var row = wrap.querySelector('.sf-error');
      if (!row) return;
      wrap.classList.add('has-error');
      wrap.classList.remove('is-valid');
      row.innerHTML = icon('alert');
      row.appendChild(document.createTextNode(message));
      controlsOf(wrap).forEach(function (c) { c.setAttribute('aria-invalid', 'true'); });
      var target = wrap.querySelector('.sf-shake-target');
      if (target && shake !== false) restart(target, 'sf-shake');
    }

    function clearError(field) {
      var wrap = fieldNodes[field.id];
      if (!wrap || !wrap.classList.contains('has-error')) return;
      wrap.classList.remove('has-error');
      wrap.querySelector('.sf-error').textContent = '';
      controlsOf(wrap).forEach(function (c) { c.removeAttribute('aria-invalid'); });
    }

    function focusField(field) {
      var wrap = fieldNodes[field.id];
      var target;
      if (field.type === 'radio') target = wrap.querySelector('input:checked') || wrap.querySelector('input');
      else target = wrap.querySelector('input, select, textarea');
      if (target) focusQuietly(target);
      scrollToView(wrap, 'center');
    }

    function validate() {
      var invalid = [];
      form.fields.forEach(function (f) {
        var message = checkField(f);
        if (message) {
          showError(f, message);
          invalid.push(f);
        } else {
          clearError(f);
        }
      });
      if (invalid.length) {
        setStatus('Please check the highlighted fields.', true);
        focusField(invalid[0]);
      }
      return !invalid.length;
    }

    function fieldOf(target) {
      var wrap = target && target.closest ? target.closest('.sf-field') : null;
      var id = wrap && wrap.getAttribute('data-field');
      return id && fieldNodes[id] === wrap ? byId[id] : null;
    }

    root.addEventListener('change', function (e) {
      refresh();
      var f = fieldOf(e.target);
      if (f && f.type !== 'file' && !checkField(f)) clearError(f);
    });
    root.addEventListener('input', function (e) {
      var f = fieldOf(e.target);
      if (!f || f.type === 'file') return;
      var wrap = fieldNodes[f.id];
      var message = checkField(f);
      if (!message) clearError(f);
      if (message || !valueOf(f)) wrap.classList.remove('is-valid');
    });
    // Valid tick and the e-mail format check on leaving a field; empty
    // required fields are only flagged on submit.
    root.addEventListener('focusout', function (e) {
      var f = fieldOf(e.target);
      if (!f || f.type === 'radio' || f.type === 'file' || f.type === 'select') return;
      var message = checkField(f);
      if (valueOf(f) && !message) {
        clearError(f);
        fieldNodes[f.id].classList.add('is-valid');
      } else if (valueOf(f) && message) {
        showError(f, message, false);
      }
    });

    form.fields.forEach(function (f) { fieldNodes[f.id].hidden = !isVisible(f); });

    // ── submit ──
    function postJson(path, body) {
      return fetch(endpoint + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          if (!r.ok) {
            var err = new Error(j.message || 'Something went wrong, please try again.');
            err.details = j;
            throw err;
          }
          return j;
        });
      });
    }

    function succeed() {
      setStatus('');
      setButton('success', 'Sent');
      setTimeout(function () {
        Object.keys(state.files).forEach(function (k) {
          state.files[k].forEach(function (item) { if (item.url) URL.revokeObjectURL(item.url); });
        });
        var title = el('h3', { className: 'sf-title', tabindex: '-1', text: form.successTitle || 'Thank you!' });
        var done = el('div', { className: 'sf-success', role: 'status' }, [
          el('div', { className: 'sf-success-badge', 'aria-hidden': 'true', html: '<span class="sf-wipe"><span>' + icon('check') + '</span></span>' }),
          title,
          el('p', { className: 'sf-success-message', text: form.successMessage || '' }),
        ]);
        root.replaceWith(done);
        focusQuietly(title);
        scrollToView(done, 'center');
      }, reduced() ? 0 : 600);
    }

    function fail(err, pending) {
      state.busy = false;
      root.classList.remove('is-busy');
      root.removeAttribute('aria-busy');
      setButton('idle', form.submitText || 'Submit');
      pending.forEach(function (p) { setProgress(p.item, 0, 'Not sent'); });
      var errs = (err.details && err.details.errors) || [];
      var flagged = [];
      errs.forEach(function (x) {
        if (fieldNodes[x.field]) {
          showError(byId[x.field], x.message);
          flagged.push(byId[x.field]);
        }
      });
      // fetch() rejects with a TypeError on network failure; its text is browser jargon.
      var message = err instanceof TypeError ? 'Connection problem. Please check your internet and press Submit again.' : err.message;
      setStatus(errs.length ? 'Please check the highlighted fields.' : message || 'Something went wrong, please try again.', true);
      if (flagged.length) focusField(flagged[0]);
    }

    root.addEventListener('submit', function (e) {
      e.preventDefault();
      if (state.busy || !validate()) return;
      state.busy = true;
      root.classList.add('is-busy');
      root.setAttribute('aria-busy', 'true');

      var values = {};
      var pending = [];
      form.fields.forEach(function (f) {
        if (!isVisible(f) || f.type === 'heading') return;
        if (f.type === 'file') state.files[f.id].forEach(function (item) { pending.push({ field: f.id, item: item }); });
        else values[f.id] = valueOf(f);
      });
      var payload = { store: store, form: formKey, submissionId: state.submissionId, values: values, files: {}, website: trapInput.value };
      var first = pending.length ? 'Uploading 1 of ' + pending.length + '…' : 'Sending…';
      setStatus(first, false, true);
      setButton('loading', first);

      var chain = Promise.resolve();
      if (pending.length) {
        pending.forEach(function (p) { setProgress(p.item, 0, 'Waiting'); });
        chain = postJson('/v1/uploads', {
          store: store,
          form: formKey,
          submissionId: state.submissionId,
          files: pending.map(function (p) { return { field: p.field, name: p.item.file.name, size: p.item.file.size }; }),
        }).then(function (res) {
          payload.token = res.token;
          return res.uploads.reduce(function (prev, up, i) {
            return prev.then(function () {
              var text = 'Uploading ' + (i + 1) + ' of ' + pending.length + '…';
              setStatus(text, false, true);
              setButton('loading', text);
              return putFile(up, pending[i].item).then(function () {
                (payload.files[up.field] = payload.files[up.field] || []).push(up.key);
              });
            });
          }, Promise.resolve());
        });
      }

      chain
        .then(function () {
          setStatus('Sending…', false, true);
          setButton('loading', 'Sending…');
          return postJson('/v1/submissions', payload);
        })
        .then(succeed)
        .catch(function (err) { fail(err, pending); });
    });

    mount.innerHTML = '';
    mount.classList.add('sf-root');
    applyAccent(mount);
    mount.appendChild(root);
  }

  // ── tabs: .button-container > .tab-button[data-target] and .tab-content[id] ──
  function initTabs(wrap) {
    var bar = wrap.querySelector('.button-container');
    var tabs = bar ? Array.prototype.slice.call(bar.querySelectorAll('.tab-button[data-target]')) : [];
    var panes = tabs.map(function (t) { return document.getElementById(t.getAttribute('data-target')); });
    if (!tabs.length || panes.indexOf(null) !== -1) return;
    wrap.setAttribute('data-sf-tabs-ready', '');
    applyAccent(wrap);

    var indicator = el('span', { className: 'sf-tab-indicator', 'aria-hidden': 'true' });
    bar.appendChild(indicator);
    bar.setAttribute('role', 'tablist');
    if (!bar.hasAttribute('aria-label') && !bar.hasAttribute('aria-labelledby')) bar.setAttribute('aria-label', 'Contact topics');

    var current = 0;
    tabs.forEach(function (tab, i) {
      if (tab.classList.contains('active')) current = i;
      if (!tab.id) tab.id = 'sf-tab-' + panes[i].id;
      tab.setAttribute('type', 'button');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panes[i].id);
      panes[i].setAttribute('role', 'tabpanel');
      panes[i].setAttribute('aria-labelledby', tab.id);
      tab.addEventListener('click', function () { select(i, false); });
    });

    function edges() {
      bar.classList.toggle('sf-fade-l', bar.scrollLeft > 1);
      bar.classList.toggle('sf-fade-r', bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 1);
    }

    function place(animate) {
      var tab = tabs[current];
      if (!animate) indicator.classList.add('sf-no-anim');
      indicator.style.transform = 'translateX(' + tab.offsetLeft + 'px) scaleX(' + (tab.offsetWidth / 100).toFixed(4) + ')';
      if (!animate) {
        void indicator.offsetWidth;
        indicator.classList.remove('sf-no-anim');
      }
      edges();
    }

    function keepInView(tab) {
      var left = tab.offsetLeft - 24;
      var right = tab.offsetLeft + tab.offsetWidth + 24;
      var target = null;
      if (left < bar.scrollLeft) target = Math.max(0, left);
      else if (right > bar.scrollLeft + bar.clientWidth) target = right - bar.clientWidth;
      if (target === null) return;
      if (typeof bar.scrollTo === 'function') bar.scrollTo({ left: target, behavior: reduced() ? 'auto' : 'smooth' });
      else bar.scrollLeft = target;
    }

    function apply(i) {
      tabs.forEach(function (tab, k) {
        var on = k === i;
        tab.classList.toggle('active', on); // the theme's own classes stay in sync
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        panes[k].classList.toggle('active', on);
      });
      current = i;
    }

    function select(i, focus) {
      if (i === current) {
        if (focus) tabs[i].focus();
        return;
      }
      var dir = i > current ? 1 : -1;
      apply(i);
      panes[i].style.setProperty('--sf-dir', String(dir));
      restart(panes[i], 'sf-pane-in');
      if (focus) tabs[i].focus();
      place(true);
      keepInView(tabs[i]);
      // Never jump the page: scroll only when the panel top is off-screen.
      var top = panes[i].getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight - 48) scrollToView(panes[i], 'start');
    }

    bar.addEventListener('keydown', function (e) {
      if (tabs.indexOf(e.target) === -1) return;
      var n = tabs.length;
      var next = null;
      if (e.key === 'ArrowRight') next = (current + 1) % n;
      else if (e.key === 'ArrowLeft') next = (current - 1 + n) % n;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = n - 1;
      if (next === null) return;
      e.preventDefault();
      select(next, true);
    });
    bar.addEventListener('scroll', edges, { passive: true });

    apply(current);
    place(false);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { place(false); });
      ro.observe(bar);
      tabs.forEach(function (t) { ro.observe(t); });
    } else {
      window.addEventListener('resize', function () { place(false); });
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sf-tabs]:not([data-sf-tabs-ready])'), initTabs);
    var mounts = document.querySelectorAll('[data-support-form]:not([data-sf-ready])');
    Array.prototype.forEach.call(mounts, function (mount) {
      mount.setAttribute('data-sf-ready', '');
      var store = mount.getAttribute('data-store');
      var endpoint = (mount.getAttribute('data-endpoint') || '').replace(/\/$/, '');
      var formKey = mount.getAttribute('data-support-form');
      loadConfig(endpoint, store)
        .then(function (cfg) {
          var form = cfg.forms[formKey];
          if (!form) throw new Error('unknown form ' + formKey);
          renderForm(mount, store, endpoint, formKey, form);
        })
        .catch(function () {
          mount.innerHTML = '';
          mount.appendChild(el('p', { className: 'sf-status', text: 'This form is temporarily unavailable. Please email us instead.' }));
        });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
